/* eslint-disable react/prop-types */
import SubmitContext from 'context/submit/submitContext'
import PageContext from 'context/page/pageContext'
import React, { useContext, useEffect, useState } from 'react'
import Select from 'react-select'
import Switch from 'react-switch'
import { Col, Row, FormGroup, Input, Alert } from 'reactstrap'
import { Controller, ErrorMessage, useFormContext } from 'react-hook-form'
import { useFormUtils } from 'views/shared/useFormUtils'
import { packagingInitialState, grainTypeOptions } from './constants'
import BSInfantCerealProduct from 'models/BSInfantCerealProduct'
import { isNotAlpha, isNotNumericDecimal } from 'context/Utilities'

const BSInfantCerealNutrition = () => {
  const submitContext = useContext(SubmitContext)
  const pageContext = useContext(PageContext)

  const { current, setCurrent, updating } = submitContext
  const { setScrollToTop } = pageContext

  const [packaging, setPackaging] = useState(packagingInitialState)

  const methods = useFormContext()

  const { setSelect, setSwitch, onChange, asterisk } = useFormUtils()

  useEffect(() => {
    setScrollToTop(true)
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (current?.category === packagingInitialState.category) {
      const {
        category,
        subCategory,
        GrainType,
        OtherGrainType,
        Iron,
        IronCertification,
        NoAdditionalIngredients,
      } = current

      setPackaging({
        category,
        subCategory,
        GrainType: {
          value: GrainType || '',
          label: GrainType.length > 0 ? GrainType : 'Grain Type',
        },
        OtherGrainType,
        Iron,
        IronCertification: IronCertification ?? false,
        NoAdditionalIngredients: NoAdditionalIngredients ?? false,
      })
    } else if (!updating) {
      setCurrent(BSInfantCerealProduct)
    }

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current])

  useEffect(() => {
    if (updating) {
      if (current?.GrainType === 'Other') {
        methods.setValue('OtherGrainType', current.OtherGrainType)
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updating])

  return (
    <Row>
      <Col xs={4}>
        <p>
          <strong>Grain Type {asterisk}</strong>
        </p>
        <Controller
          as={Select}
          className="react-select primary"
          classNamePrefix="react-select"
          options={grainTypeOptions}
          name="GrainType"
          control={methods.control}
          rules={{ required: true }}
          placeholder={'Select Grain Type'}
          onChange={([selected]) => {
            setSelect(selected, 'GrainType')
            return selected
          }}
        />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="GrainType"
            message={'⚠ Please select a grain type.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
        <br />
        <br />
        <br />
        {current?.GrainType === 'Other' && (
          <FormGroup>
            <p>
              <strong>Other Type (Please Specify) {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="OtherGrainType"
              placeholder={'Other Type'}
              onKeyDown={e => {
                if (isNotAlpha(e.key) && e.key !== '-' && e.key !== ' ') {
                  e.preventDefault()
                }
              }}
              control={methods.control}
              rules={{
                maxLength: {
                  value: 30,
                  message: '⚠ Other Type exceeds 30 characters.',
                },
                required: {
                  value: true,
                  message: '⚠ Other Type is required.',
                },
              }}
              onChange={([event]) => {
                onChange(event)
                return event
              }}
            />
            <p className={'text-danger'}>
              <ErrorMessage
                errors={methods.errors}
                name="OtherGrainType"
                as={<Alert color={'danger'} className={'mt-3'} />}
              />
            </p>
          </FormGroup>
        )}
      </Col>
      <Col xs={4}>
        <FormGroup>
          <p>
            <strong>Iron (mg) per 100g dry cereal {asterisk}</strong>
          </p>
          <Controller
            as={Input}
            type="number"
            step="0.01"
            name="Iron"
            placeholder={'Min 45mg iron per 100g dry cereal'}
            onKeyDown={e => {
              if (isNotNumericDecimal(e.key)) {
                e.preventDefault()
              }
            }}
            control={methods.control}
            rules={{
              maxLength: {
                value: 6,
                message:
                  '⚠ Iron (mg) per 100g dry cereal exceeds 6 characters.',
              },
              min: {
                value: 45,
                message:
                  '⚠ Iron (mg) per 100g dry cereal is below the 45mg minimum allowed.',
              },
              required: {
                value: true,
                message: '⚠ Please enter Iron (mg) per 100g dry cereal.',
              },
              pattern: {
                value: /^\d+(\.\d{1,2})?$/,
                message: '⚠ Maximum two decimal places allowed.',
              },
            }}
            onChange={([event]) => {
              onChange(event)
              return event
            }}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="Iron"
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
      </Col>
      <Col xs={4}>
        <p>
          <strong>
            I certify that this product contains a minimum of 45mg of iron per
            100 grams of dry cereal. {asterisk}
          </strong>
        </p>
        <Controller
          as={Switch}
          name="IronCertification"
          id="IronCertification"
          onChange={([checked, event, id]) => {
            setSwitch(checked, event, id)
            return checked
          }}
          control={methods.control}
          defaultValue={packaging.IronCertification}
          rules={{
            validate: IronCertification => IronCertification === true,
          }}
        />
        <br />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="IronCertification"
            message={
              '⚠ You must certify that this product contains a minimum of 45mg of iron per 100 grams of dry cereal.'
            }
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
        <br />
        <hr />
        <p>
          <strong>
            I certify that this product does not contain added ingredients, such
            as formula, milk, fruits, vegetables, sugars, sweeteners,
            probiotics, or DHA. {asterisk}
          </strong>
        </p>
        <Controller
          as={Switch}
          name="NoAdditionalIngredients"
          id="NoAdditionalIngredients"
          onChange={([checked, event, id]) => {
            setSwitch(checked, event, id)
            return checked
          }}
          control={methods.control}
          defaultValue={packaging.NoAdditionalIngredients}
          rules={{
            validate: NoAdditionalIngredients =>
              NoAdditionalIngredients === true,
          }}
        />
        <br />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="NoAdditionalIngredients"
            message={
              '⚠ You must certify that this product does not contain added ingredients, such as formula, milk, fruits, vegetables, sugars, sweeteners, probiotics, or DHA.'
            }
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
      </Col>
    </Row>
  )
}

export default BSInfantCerealNutrition
